import { useApps } from '@auth/useApps'
import {
  AccountTypeEnum,
  getDelegatorPoints,
  listPointPresets,
  PointPresetTypeEnum,
} from '@kudos/http-client'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import CheckmarkChip from '@shared/components/CheckmarkChip'
import FieldLabel from '@shared/forms/FieldLabel'
import NumberField from '@shared/forms/NumberField'
import { AccountPermission, AppPermission, hasPermission } from '@shared/permissions'
import { useQuery } from '@tanstack/react-query'
import useRecognitionModal from '@tenant/recognition/RecognitionModal/useRecognitionModal'
import { T, useT } from '@transifex/react'
import { useEffect, useMemo, useState } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import DelegatorPointsEstimateDisplay from './DelegatorPointsEstimateDisplay'
import PointsEstimateDisplay from './PointsEstimateDisplay'
import { RecognitionFormData } from './useSendRecognitionForm'

type ChipSelect = { id: string; label: string }

const PointsSelectionField: React.FC = () => {
  const t = useT()
  const { dialogData } = useRecognitionModal()
  const delegatorId = dialogData?.delegatorId
  const [disabledValues, setDisabledValues] = useState<string[]>([])
  const [isUsingCustomAmount, setIsUsingCustomAmount] = useState<boolean>(false)
  const [recipientCount, setRecipientCount] = useState<number>(0)
  const [allocationPoints, setAllocationPoints] = useState<number>(0)
  const [hasAppPermission] = useApps()

  const { data: delegatorAccounts, isFetched: isDelegatorPointsFetched } = useQuery({
    queryKey: ['delegatorPoints', delegatorId],
    queryFn: () => getDelegatorPoints(delegatorId ?? ''),
    enabled: !!delegatorId,
  })

  const delegatorAllocationAccount = delegatorAccounts?.find(
    account => account.type === AccountTypeEnum.ALLOCATION,
  )

  const canSendDelegatedPoints =
    delegatorAllocationAccount &&
    hasPermission(delegatorAllocationAccount, AccountPermission.SendDelegatedPoints)

  const canSendPoints = delegatorId
    ? canSendDelegatedPoints
    : hasAppPermission('pointsApp', AppPermission.PointsAppSendPoints)

  const { data: pointPresetsData, isLoading: isLoadingPresets } = useQuery({
    queryKey: ['pointPresets', 'recognition'],
    queryFn: () => listPointPresets({ typeIn: [PointPresetTypeEnum.RECOGNITION] }),
  })

  const recognitionPreset = pointPresetsData?.find(
    preset => preset.type === PointPresetTypeEnum.RECOGNITION,
  )

  const points: ChipSelect[] = useMemo(() => {
    if (!recognitionPreset?.presetPoints) return []

    // Create points array from API data, always include 0
    const presetPoints = [0, ...recognitionPreset.presetPoints]
      .sort((a, b) => a - b)
      .map(value => ({
        id: value.toString(),
        label: value.toString(),
      }))

    return presetPoints
  }, [recognitionPreset?.presetPoints])

  const allowCustomPoints = recognitionPreset?.allowCustomPoints ?? true

  const { control, setValue, setError, trigger } = useFormContext<RecognitionFormData>()

  const {
    field: { value: amount, onChange },
  } = useController({ control, name: 'amount' })

  const sendTo = useWatch({ control, name: 'sendTo' })

  const handleRecipientCountChange = (count: number) => {
    setRecipientCount(count)
  }

  const handleAllocationPointsChange = (points: number) => {
    setAllocationPoints(points)
  }

  const maxPerRecipient = useMemo(() => {
    if (recipientCount && recipientCount > 0) {
      return Math.floor(allocationPoints / recipientCount)
    } else {
      return allocationPoints
    }
  }, [allocationPoints, recipientCount])

  useEffect(() => {
    if (amount > maxPerRecipient) {
      setError('amount', {
        type: 'maxLength',
        message: t('Enter a point amount that is {maxPerRecipient} or less', { maxPerRecipient }),
      })
    } else {
      // If the amount is less than or equal to max points per recipient
      // let's trigger revalidation on the amount field as it has
      // other validations rules defined in the schema that
      // may need to be applied.
      trigger('amount')
    }
  }, [amount, maxPerRecipient, setError])

  useEffect(() => {
    if (isLoadingPresets || points.length === 0) return
    const setPointOptions = points.map(p => parseInt(p.id, 10))
    if (!setPointOptions.includes(amount)) {
      setIsUsingCustomAmount(true)
    }
  }, [amount, isLoadingPresets, points])

  useEffect(() => {
    updateDisabledChips()
  }, [recipientCount, allocationPoints])

  const updateDisabledChips = () => {
    // A list of all the values we need to disable.
    const calculatedDisabled = points.filter(p => Number(p.id) > maxPerRecipient).map(p => p.id)

    // If we've not set a custom amount, and their total exceeds,
    // then automatically set to the next biggest one on the list.
    if (!isUsingCustomAmount && amount * recipientCount > allocationPoints) {
      const maxAllowed = points
        .filter((c: ChipSelect) => !calculatedDisabled.includes(c.id))
        .sort((a: ChipSelect, b: ChipSelect) => (Number(a.id) < Number(b.id) ? -1 : 1))
        .pop()

      !!maxAllowed && setValue('amount', parseInt(maxAllowed.id, 10))
    }

    setDisabledValues(calculatedDisabled)
  }

  const handleFixedSelection = (value: number) => {
    onChange(value)
    setIsUsingCustomAmount(false)
  }

  const openCustomSelection = () => {
    onChange()
    setIsUsingCustomAmount(true)
  }

  if (!canSendPoints) return null

  if (isLoadingPresets) {
    return (
      <Box pb={2}>
        <Stack useFlexGap direction="row" flexWrap="wrap" spacing={1}>
          {Array.from(new Array(5)).map((_, value) => (
            <Skeleton variant="circular" key={value} width={42} height={36} />
          ))}
        </Stack>
      </Box>
    )
  }

  return (
    <Stack useFlexGap gap={1}>
      <FieldLabel>
        <T _str="Add points" />
      </FieldLabel>
      <Box pb={2}>
        <Stack useFlexGap direction="row" flexWrap="wrap" spacing={1}>
          {points.map(p => (
            <Tooltip
              id={`tooltipCheckmarkChip-${p.id}`}
              key={p.id}
              title={disabledValues?.includes(p.id) ? <T _str="Not enough points" /> : null}
              placement="top">
              <Stack>
                <CheckmarkChip
                  id={`pointsAmountChip-${p.id}`}
                  aria-labelledby={`tooltipCheckmarkChip-${p.id}`}
                  checked={!isUsingCustomAmount && amount === Number(p.id)}
                  label={p.label}
                  value={p.id}
                  disabled={disabledValues?.includes(p.id) ?? false}
                  onChange={value => handleFixedSelection(parseInt(value, 10))}
                />
              </Stack>
            </Tooltip>
          ))}
          {allowCustomPoints && (
            <CheckmarkChip
              id={'pointsAmountChip-other'}
              checked={isUsingCustomAmount}
              label="Other"
              value=""
              disabled={allocationPoints === 0}
              onChange={openCustomSelection}
            />
          )}
        </Stack>
      </Box>
      {isUsingCustomAmount && allowCustomPoints && (
        <Stack mb={1}>
          <NumberField
            width={150}
            fullWidth
            control={control}
            name="amount"
            label={<T _str="Custom point amount" />}
            labelStyle={{ mt: 0 }}
            decimalScale={0}
            value={amount || ''}
            onChange={event => onChange(parseInt(event.target.value, 10) || 0)}
          />
        </Stack>
      )}
      {delegatorId && delegatorAccounts ? (
        <DelegatorPointsEstimateDisplay
          amount={amount}
          sendTo={sendTo}
          delegatorId={delegatorId}
          onRecipientCountChange={handleRecipientCountChange}
          onAllocationPointsChange={handleAllocationPointsChange}
          delegatorAccounts={delegatorAccounts}
          isDelegatorPointsFetched={isDelegatorPointsFetched}
        />
      ) : (
        <PointsEstimateDisplay
          amount={amount}
          sendTo={sendTo}
          onRecipientCountChange={handleRecipientCountChange}
          onAllocationPointsChange={handleAllocationPointsChange}
        />
      )}
    </Stack>
  )
}

export default PointsSelectionField
